import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchNonDeliveryDaysRequest, addNonDeliveryDays } from 'actions/action-shipping';
import { useDispatch, useSelector } from 'react-redux';
import { useTypedSelector } from 'hooks/use-typed-selector';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import {
  INonDeliveryDays,
  IRole,
  IPermissions,
  ResourceName,
  ResourceAction,
  SnackbarState,
  SnackbarSeverity,
  SnackbarMessages,
} from './non-delivery-days.interface';
import NonDeliveryDaysManager from './non-delivery-days';

export default (): JSX.Element => {
  const [selectedCustomerId, setSelectedCustomerId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentNonDeliveryDays, setCurrentNonDeliveryDays] = useState<INonDeliveryDays | null>(
    null,
  );
  const [initialNonDeliveryDays, setInitialNonDeliveryDays] = useState<INonDeliveryDays | null>(
    null,
  );
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: SnackbarSeverity.SUCCESS,
  });
  const dispatch = useDispatch();
  const { nonDeliveryDays: customerNonDeliveryDays } = useSelector((state: any) => state.shipping);
  const { roles, permissions } = useTypedSelector(state => state.auth.currentUser) as {
    roles: IRole[];
    permissions: IPermissions;
  };

  const hasAddUpdateNonDeliveryDaysPermission = useMemo(() => {
    const hasAddNonDeliveryDaysResourcePermission = roles
      .filter(role => role.resource_name === ResourceName.NON_DELIVERY_DAYS)
      .some(
        item =>
          item.resource_action === ResourceAction.CREATE ||
          item.resource_action === ResourceAction.UPDATE,
      );
    const hasCustomerLevelPermission =
      permissions?.application_manager_users_manage_customer_level?.customers?.some(
        (customer: string) => Number(customer) === selectedCustomerId,
      );

    return !!(hasAddNonDeliveryDaysResourcePermission && hasCustomerLevelPermission);
  }, [roles, selectedCustomerId, permissions]);

  const fetchNonDeliveryDays = useCallback(async () => {
    setLoading(true);
    dispatch(fetchNonDeliveryDaysRequest(selectedCustomerId));
  }, [dispatch, selectedCustomerId]);

  useEffect(() => {
    if (customerNonDeliveryDays) {
      setCurrentNonDeliveryDays(customerNonDeliveryDays);
      setInitialNonDeliveryDays(customerNonDeliveryDays);
    }
  }, [customerNonDeliveryDays]);

  useEffect(() => {
    if (selectedCustomerId && selectedCustomerId !== 0) {
      fetchNonDeliveryDays();
    }
  }, [selectedCustomerId, fetchNonDeliveryDays]);

  const handleCheckboxChange = (field: keyof INonDeliveryDays, day: string, checked: boolean) => {
    setCurrentNonDeliveryDays(prev => {
      if (!prev) return prev;

      const prevArray = Array.isArray(prev[field]) ? (prev[field] as string[]) : [];
      const newArray = checked ? [...prevArray, day] : prevArray.filter(d => d !== day);
      return {
        ...prev,
        [field]: newArray,
      };
    });
  };

  const submitRequestNonDeliveryDays = useCallback(async () => {
    if (currentNonDeliveryDays) {
      try {
        const action = addNonDeliveryDays(currentNonDeliveryDays);
        await action.payload;
        dispatch(action);
        setSnackbar({
          open: true,
          message: SnackbarMessages.SAVE_SUCCESS,
          severity: SnackbarSeverity.SUCCESS,
        });
      } catch (e) {
        setSnackbar({
          open: true,
          message: SnackbarMessages.SAVE_ERROR,
          severity: SnackbarSeverity.ERROR,
        });
      }
    }
  }, [dispatch, currentNonDeliveryDays]);

  const isChanged = useMemo(() => {
    if (!initialNonDeliveryDays || !currentNonDeliveryDays) return false;
    return JSON.stringify(initialNonDeliveryDays) !== JSON.stringify(currentNonDeliveryDays);
  }, [initialNonDeliveryDays, currentNonDeliveryDays]);

  return (
    <>
      <NonDeliveryDaysManager
        loading={loading}
        nonDeliveryDays={currentNonDeliveryDays}
        selectedCustomerId={selectedCustomerId}
        setSelectedCustomerId={setSelectedCustomerId}
        submitRequestNonDeliveryDays={submitRequestNonDeliveryDays}
        handleCheckboxChange={handleCheckboxChange}
        hasAddUpdateNonDeliveryDaysPermission={hasAddUpdateNonDeliveryDaysPermission}
        isChanged={isChanged}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};
